import { useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSnapshot } from 'valtio';

import Link from '@cartwhl/common/components/Link';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import { Project } from '@cartwhl/common/types';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Logo from '@cartwhl/ui/atoms/Logo';
import LogoMark from '@cartwhl/ui/atoms/LogoMark';
import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import CopyIcon from '@cartwhl/ui/icons/CopyIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import EmbedIcon from '@cartwhl/ui/icons/EmbedIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import ShareIcon from '@cartwhl/ui/icons/ShareIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';
import UserMenu from '@cartwhl/ui/organisms/UserMenu';

import {
  animationGenerationStore,
  authStore,
  navigationStore,
  noticesStore,
  posingStore,
  projectStore,
  userStore,
} from 'stores';
import { LEGACY_PROJECT_ID } from 'stores/projectStore';

import EditableProjectName from './EditableProjectName';
import EditableSessionName from './EditableSessionName';
import NewProjectDropdown from './NewProjectDropdown';
import SessionDropdownMobile from './SessionDropdownMobile';

export const CANVAS_CAPTURE_PARAM_NAME = 'canvasCapture';

export default function Header({ showBorder }: { showBorder?: boolean }) {
  const { currentAnimationNumber, jobData } = useSnapshot(
    animationGenerationStore.state,
  );
  const {
    currentProject,
    motion: { addExistingMotionSubmitting },
  } = useSnapshot(projectStore.state);
  const { authStatus, userID } = useSnapshot(authStore.state);
  const { userInfo } = useSnapshot(userStore.state);
  const [signInRedirect, setSignInRedirect] = useState('');
  const { theme } = useSnapshot(userStore.state);
  const { path } = useSnapshot(navigationStore.state);
  const { poseHasBeenAdjusted } = useSnapshot(posingStore.state);
  const projectJobIDs = currentProject.data
    ? projectStore.getJobIDsFromProjectTimeline(currentProject.data as Project)
    : [];

  const hasPoseInPath = window.location.pathname.includes('pose');
  const isProject = path.startsWith('/project/');
  const isProjectsPage = path.startsWith('/projects');
  const isHomeRoute = navigationStore.matchesRoute('home');
  const isLegacyProject = isHomeRoute;
  const hasPoseModeAccess = true;

  useHotkeys('shift+l', () => {
    if (theme === 'dark') {
      userStore.setTheme('light');
    } else {
      userStore.setTheme('dark');
    }
  });

  // Until we have a better "view only state. If you're viewing somebody else's job we hide the prompt bar"
  const isCreator =
    (jobData && userID === jobData.createdBy) ||
    (currentProject.data && userID === currentProject.data?.createdBy);

  // TODO: Use navigation store for params or let's upgrade to react-router (or equivalent)
  const params = new URLSearchParams(window.location.search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (params.has('jobID') && params.has('animationNumber')) {
      const redirectParams = new URLSearchParams({
        jobID: params.get('jobID')!,
        animationNumber: params.get('animationNumber')!,
      });

      // INFO: Double encoding is intentional here so that it is handled correctly passing
      // through Stytch's RedirectURL mechanism
      const encodedParams = encodeURIComponent(
        encodeURIComponent(`/home?${redirectParams.toString()}`),
      );
      setSignInRedirect(encodedParams);
    }
  }, [params]);

  // check for deciding whether to redirect users.
  // anonymous users are auto redirected, authenticated users need to wait until
  // their info has been loaded (which we check via existence of their user ID)
  useEffect(() => {
    const shouldRedirect =
      authStatus === 'anonymous' ||
      (authStatus === 'user' && userInfo.userID && !hasPoseModeAccess);
    if (hasPoseInPath && shouldRedirect) {
      setTimeout(() => {
        noticesStore.info(
          "Posing is not yet available for you, but is coming soon! We're automatically redirecting you back to the main studio page",
        );
        navigationStore.navigate('/home');
      }, 250);
    }
  }, [hasPoseInPath, hasPoseModeAccess, authStatus, userInfo.userID]);

  const selectedVariationNumber = Number(currentAnimationNumber);
  const canCreateFromLegacyMotion =
    isLegacyProject && jobData && Number.isFinite(selectedVariationNumber);

  const handleCreateAsNewProject = async () => {
    if (!canCreateFromLegacyMotion) {
      noticesStore.info('Select a legacy motion first.');
      return;
    }

    await projectStore.addExistingMotionToProject({
      createAsNewProject: true,
      existingJobID: jobData.jobID,
      existingVariationNumber: selectedVariationNumber,
    });
  };

  return (
    <div
      className={`relative top-0 right-0 flex shrink-0 basis-14 items-center gap-1 px-5 lg:basis-16 lg:gap-2 lg:px-8 xl:px-11 ${
        showBorder ? 'border-gray-30 dark:border-gray-75 border-b' : ''
      }`}
    >
      <div className="relative z-20 flex flex-1 items-center gap-4">
        {!isProjectsPage && (
          <Link
            href="/projects"
            onClick={() => {
              projectStore.resetCurrentProject();
              animationGenerationStore.resetJobData();
            }}
          >
            <Button variant="secondary" size="sm" icon={<ChevronLeftIcon />} />
          </Link>
        )}

        {!isProjectsPage && (jobData || hasPoseModeAccess) ? (
          <div className="flex min-w-0 items-center gap-3 text-left lg:gap-7">
            <Logo className="hidden h-[29px] w-[155px] flex-none md:block" />
            <LogoMark className="h-5 w-5 flex-none md:hidden" />
            {authStatus === 'user' && (
              <>
                {/* On Desktop, always show New motion button */}
                {!isLegacyProject && (
                  <NewProjectDropdown
                    className="hidden lg:flex"
                    hasPoseModeAccess={hasPoseModeAccess}
                  />
                )}
                {/* On Mobile, show dropdown if creator, otherwise show New project dropdown */}
                {isCreator && jobData ? (
                  <SessionDropdownMobile
                    hasPoseModeAccess={hasPoseModeAccess}
                    sessionName={jobData.sessionName}
                    jobID={jobData.jobID}
                  />
                ) : !isLegacyProject ? (
                  <NewProjectDropdown
                    className="flex lg:hidden"
                    hasPoseModeAccess={hasPoseModeAccess}
                  />
                ) : null}
              </>
            )}
          </div>
        ) : (
          <Logo className="w-32 flex-none text-left lg:w-[155px]" />
        )}
      </div>

      {(jobData || currentProject) &&
        isCreator &&
        !isProjectsPage &&
        !isLegacyProject && (
        <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
          {jobData ? (
            <EditableSessionName
              jobID={jobData.jobID}
              sessionName={jobData.sessionName}
            />
          ) : currentProject.data ? (
            <EditableSessionName
              jobID={currentProject.data?.projectID ?? ''}
              sessionName={currentProject.data?.projectName ?? ''}
              isPoseProject={true}
            />
          ) : null}
        </div>
      )}

      {isLegacyProject && (
        <div className="relative z-10 flex min-w-0 flex-1 items-center justify-center overflow-hidden">
          <div className="flex min-w-0 flex-1 items-center justify-center">
            <Text variant="studio/title/md" className="truncate">
              Legacy project
            </Text>
          </div>
        </div>
      )}
      {isProject &&
        currentProject.data &&
        currentProject.data.projectID !== LEGACY_PROJECT_ID && (
          <div className="relative z-10 flex min-w-0 flex-1 items-center justify-center overflow-hidden">
            <EditableProjectName
              projectId={currentProject.data.projectID ?? ''}
              projectName={currentProject.data.projectName ?? 'Untitled'}
            />
          </div>
        )}

      <div className="lg:grap-4 relative z-20 flex flex-1 items-center justify-end gap-2">
        {authStatus === 'user' && <UserMenu />}
        <div className="flex h-10 items-center gap-2">
          <Dropdown
            trigger={
              <Button
                variant="secondary"
                size="sm"
                className="text-gray-90 lg:hidden"
                icon={<ThreeDotsIcon />}
              />
            }
          >
            {!isLegacyProject && (
              <Dropdown.Item
                key="Share"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  noticesStore.info('URL Copied to clipboard!');
                }}
              >
                <span className="m-0 flex flex-row flex-nowrap items-center gap-2">
                  <ShareIcon />
                  Share
                </span>
              </Dropdown.Item>
            )}

            <Dropdown.Item
              key={isLegacyProject ? 'Create as new project' : 'Download'}
              onClick={() => {
                if (isLegacyProject) {
                  handleCreateAsNewProject();
                  return;
                }

                navigationStore.setModal(StudioModalName.DOWNLOAD);
              }}
            >
              <span className="m-0 flex flex-row flex-nowrap items-center gap-2">
                {isLegacyProject ? <PlusIcon /> : <DownloadIcon />}
                {isLegacyProject ? 'Create as new project' : 'Download'}
              </span>
            </Dropdown.Item>
          </Dropdown>

          {authStatus === 'user' && jobData && !isLegacyProject && (
            <div className="hidden h-10 items-center lg:flex">
              <Dropdown
                trigger={
                  <Button
                    variant="secondary"
                    size="sm"
                    className="text-gray-90"
                    icon={<ShareIcon />}
                  />
                }
              >
                <Dropdown.Item
                  icon={<CopyIcon />}
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    noticesStore.info('URL Copied to clipboard!');
                  }}
                >
                  Copy link
                </Dropdown.Item>
                <Dropdown.Item
                  icon={<EmbedIcon />}
                  onClick={() => {
                    noticesStore.info('Embed coming soon!');
                  }}
                >
                  Embed
                </Dropdown.Item>
              </Dropdown>
            </div>
          )}

          {(authStatus === 'user' || params.has(CANVAS_CAPTURE_PARAM_NAME)) &&
            (jobData || poseHasBeenAdjusted || projectJobIDs.length > 0) &&
            !isProjectsPage && (
              <Button
                className="hidden lg:flex"
                variant="primary"
                size="sm"
                id={isLegacyProject ? 'create-as-new-project' : 'download-video-open'}
                onClick={() => {
                  if (isLegacyProject) {
                    handleCreateAsNewProject();
                    return;
                  }

                  navigationStore.setModal(StudioModalName.DOWNLOAD);
                }}
                icon={isLegacyProject ? <PlusIcon /> : <DownloadIcon />}
                disabled={isLegacyProject && addExistingMotionSubmitting}
                alignIcon="end"
              >
                {isLegacyProject ? 'Create as new project' : 'Download'}
              </Button>
            )}

          {authStatus === 'anonymous' && (
            <>
              <Link
                href={getHomepageUrl(
                  `/login?redirectTo=studio${signInRedirect}`,
                )}
                className="mx-2"
              >
                <Button variant="tertiary" className="flex">
                  Sign in
                </Button>
              </Link>
              <Link href={getHomepageUrl('/sign-up')}>
                <Button variant="primary" className="flex">
                  Sign Up
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
